import { useMemo, useState } from "react";
import {
  todayGapsFromFit,
  type CatalogMuscleSlug,
  type ModelFitGaps,
} from "@heraion/shared";

import type { MuscleCoverage } from "./muscleCoverage";
import { sunkSlugsAt, WHOLE_CATALOG } from "./muscleCoverageProposal";
import type { ProgressDeps } from "./types";
import { coverageOf, useMuscleCoverage } from "./useMuscleCoverage";
import { useSessionResultsFromDeps } from "./useSessionResults";

// #492 F5, «plan the gaps»: today's draft from three doors. The bodyweight
// toggle reaches the engine one way, ProposalScope.bodyweightOnly (F4).

/** The deps with the bodyweight filter on the athlete's place. Off: as given. */
export const withBodyweightOnly = (deps: ProgressDeps, on: boolean): ProgressDeps =>
  on
    ? { ...deps, trainingPlace: { ...(deps.trainingPlace ?? WHOLE_CATALOG), bodyweightOnly: true } }
    : deps;

/** The muscles the filter left with no exercise, in the rows' order. */
export const noBodyweightMuscles = (coverage: MuscleCoverage | null): CatalogMuscleSlug[] =>
  (coverage?.rows ?? [])
    .filter((r) => r.proposal?.kind === "noBodyweight")
    .map((r) => r.landmark.muscle);

/** The coverage with the toggle's state: Progress and the Create hub. */
export function usePlanGapsCoverage(deps: ProgressDeps) {
  const [bodyweightOnly, setBodyweightOnly] = useState(false);
  const scoped = useMemo(() => withBodyweightOnly(deps, bodyweightOnly), [deps, bodyweightOnly]);
  return { ...useMuscleCoverage(scoped), bodyweightOnly, setBodyweightOnly };
}

/** The program card's fit (modelFitGaps), already scoped by its toggle. */
export type PlanGapsLauncher = (input: {
  fit: Pick<ModelFitGaps, "belowMev" | "recommendations">;
  bodyweightOnly: boolean;
}) => void;

/** The Home program card's door: the cycle's gaps still open this week
 *  (todayGapsFromFit), at the athlete's place, read on the tap. */
export function usePlanGapsLauncher(deps: ProgressDeps): PlanGapsLauncher | undefined {
  const { results, catalog } = useSessionResultsFromDeps(deps);
  return useMemo(() => {
    const onBuildToday = deps.onBuildToday;
    if (!onBuildToday || !results || !catalog) return undefined;
    return ({ fit, bodyweightOnly }) => {
      const week = coverageOf(results, catalog, Date.now(), deps)?.byMuscle;
      const sunk = sunkSlugsAt(deps.trainingPlace, results);
      const gaps = todayGapsFromFit(fit, {
        weekSets: week?.last7DaysSets ?? new Map(),
        ...(week ? { lastTrainedMs: week.lastTrainedMs } : {}),
        ...(sunk.size > 0
          ? { slugs: new Set(catalog.filter((e) => !sunk.has(e.slug)).map((e) => e.slug)) }
          : {}),
      });
      onBuildToday(gaps, catalog, { bodyweightOnly });
    };
  }, [deps, results, catalog]);
}
